import { store } from 'store'
import {
  PLENTY_PRODUCTS,
  Product,
  PRODUCT_BILLING_OPTIONS,
  PRODUCT_CONTACT_OPTIONS,
  SOURCE_TYPES,
  PRODUCTS,
  PRODUCTS_CONFIG,
  Region,
  PRODUCT_CONFIG_OPTIONS,
  AvailableSalesProducts,
  Language as ProductNameLanguage
} from '@boltenergy-be/shared'
import { checkEnforcePlenty } from 'features/simulation/utils'
import { AvailableProducts } from 'types/parameters'
import { getProductPromoForDate } from 'components/promo-code/utils'

/**
 * Helper function that checks if a product is digital only
 * @param {Product} product
 * @returns {{[PRODUCT_CONFIG_OPTIONS.billingOptions]: boolean, [PRODUCT_CONFIG_OPTIONS.contactOptions]: boolean}}
 */
export const checkIfProductIsDigitalOnly = (
  product?: Product
): { [PRODUCT_CONFIG_OPTIONS.billingOptions]: boolean; [PRODUCT_CONFIG_OPTIONS.contactOptions]: boolean } => {
  if (!product) {
    return {
      [PRODUCT_CONFIG_OPTIONS.contactOptions]: false,
      [PRODUCT_CONFIG_OPTIONS.billingOptions]: false
    }
  }

  const contactOptions = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.contactOptions]
  const billingOptions = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.billingOptions]

  return {
    [PRODUCT_CONFIG_OPTIONS.contactOptions]: contactOptions.length === 1 && contactOptions[0] === PRODUCT_CONTACT_OPTIONS.digital,
    [PRODUCT_CONFIG_OPTIONS.billingOptions]: billingOptions.length === 1 && billingOptions[0] === PRODUCT_BILLING_OPTIONS.digital
  }
}

/**
 * Helper function that returns the default products for a given region
 * @param {{
 *   region: Region
 *   isSales?: boolean
 *   isEarlyBird?: boolean
 * }} payload
 * @returns {Product[]}
 */
export const getUsableProducts = ({
  region,
  isEarlyBird = false,
  withPlenty = false
}: {
  region: Region
  isEarlyBird?: boolean
  withPlenty?: boolean
}): Product[] => {
  const { sourceType, availableProducts, plentyOpportunityId } = store.getState().marketing
  const { propositions } = store.getState().customer

  // If there are available regions from the URL, use those to determine the products instead of the config
  if (availableProducts && region in availableProducts) {
    return availableProducts[region]
  }

  // Always skip early bird & go products, they are replaced in the .reduce below
  const productsToSkip: Product[] = [PRODUCTS.earlyBirdOffline, PRODUCTS.earlyBirdOnline, PRODUCTS.go]

  // Either use Plenty or Bolt products
  const enforcePlenty = checkEnforcePlenty({ region, availableProducts })
  const simulateWithPlenty = !!plentyOpportunityId || propositions?.showPlentyPropositions || withPlenty || enforcePlenty

  if (!simulateWithPlenty) {
    productsToSkip.push(...PLENTY_PRODUCTS)
  }

  if (enforcePlenty) {
    productsToSkip.push(PRODUCTS.fix, PRODUCTS.variableOnline, PRODUCTS.variableOffline)
  }

  // Loop over products in the config and return the ones that are available in the given region
  return Object.keys(PRODUCTS_CONFIG).reduce((productsForGivenRegion: Product[], product: Product) => {
    const availableIn = PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.availableInRegions]

    // Skip product if it's in the skippable list
    if (productsToSkip.includes(product)) return productsForGivenRegion

    // Skip the product for sales user if it's not in the available products list
    if (availableProducts && !availableProducts[region]?.includes(product as AvailableSalesProducts)) return productsForGivenRegion

    // Add product if it's available in the given region
    if (availableIn.includes(region)) {
      const shouldChangeToEarlyBird =
        !simulateWithPlenty &&
        sourceType !== SOURCE_TYPES.push &&
        isEarlyBird &&
        [PRODUCTS.variableOffline, PRODUCTS.variableOnline].some((value) => value === product)

      productsForGivenRegion.push(shouldChangeToEarlyBird ? convertProductToEarlyBird(product) : product)
    }

    return productsForGivenRegion
  }, [])
}

/**
 * Helper function that converts a variable product to early bird product
 * @param {Product} product
 * @returns {Product}
 */
const convertProductToEarlyBird = (product: Product): Product => {
  switch (product) {
    case PRODUCTS.variableOffline:
      return PRODUCTS.earlyBirdOffline
    case PRODUCTS.variableOnline:
      return PRODUCTS.earlyBirdOnline
    default:
      return product
  }
}

/**
 * Check if the Plenty promo code can be applied to the given product
 * @param {Product} product
 * @returns {boolean}
 */
export const checkCanApplyPlentyPromo = (product: Product): boolean => {
  const { simulation } = store.getState().customer.inputs
  const { promoCode, referralCode, region } = simulation
  const activePromo = getProductPromoForDate(product)

  return !promoCode && !referralCode && !!activePromo && !!region && activePromo.availableInRegion.includes(region)
}

/**
 * Checks if the given product is available in the given region. If available products is present, use that region logic instead of PRODUCTS_CONFIG.
 * @param {Region | undefined} region
 * @param {Product | undefined} product
 * @param {AvailableProducts | undefined} availableProducts
 * @returns {boolean}
 */
export const checkProductIsAvailableInRegion = (
  region: Region | undefined,
  product: Product | undefined,
  availableProducts: AvailableProducts | undefined
): boolean => {
  if (!region || !product) return false

  if (availableProducts && region in availableProducts) {
    return (availableProducts[region] as Array<Product>).includes(product)
  }

  return PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.availableInRegions].includes(region)
}

/**
 * Returns the product name for the given locale
 * @param {Product | undefined} product
 * @param {string | undefined} locale
 * @returns {string}
 */
export const getProductName = (product: Product | undefined, locale: string | undefined): string => {
  if (!product) return ''

  return PRODUCTS_CONFIG[product][PRODUCT_CONFIG_OPTIONS.name][locale?.toUpperCase() as ProductNameLanguage] ?? ''
}
